// Tiny DOM + formatting helpers. No framework — just typed createElement sugar.

type Child = Node | string | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Partial<Omit<HTMLElementTagNameMap[K], 'style'>> & {
    class?: string;
    // Inline styles as a CSS string (applied via setAttribute).
    style?: string;
    dataset?: Record<string, string>;
    onClick?: (ev: MouseEvent) => void;
  } = {},
  children: Child[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  const { class: className, style, dataset, onClick, ...rest } = props;
  if (className) node.className = className;
  if (style) node.setAttribute('style', style);
  if (dataset) {
    for (const [k, v] of Object.entries(dataset)) node.dataset[k] = v;
  }
  if (onClick) node.addEventListener('click', onClick as EventListener);
  Object.assign(node, rest);
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    node.append(child instanceof Node ? child : document.createTextNode(child));
  }
  return node;
}

export function clear(node: HTMLElement): void {
  node.replaceChildren();
}

// Breathing room between the sticky header and the scrolled-to panel, so the
// panel's top edge does not touch the header's border.
const HEADER_GAP_PX = 8;

/**
 * Scrolls `target` to the top of the window, below the sticky header. The
 * header's height changes with the window's width (its nav wraps), so it is
 * measured now rather than written into the CSS.
 */
export function scrollBelowHeader(target: HTMLElement): void {
  const header = document.querySelector<HTMLElement>('.app-header');
  target.style.scrollMarginTop = `${(header?.offsetHeight ?? 0) + HEADER_GAP_PX}px`;
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('en-US');
}

export function fmtDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function fmtMoneyCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  const dollars = cents / 100;
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: Math.abs(dollars) >= 1000 ? 0 : 2,
  });
}

export function fmtPct(
  fraction: number | null | undefined,
  digits = 1,
): string {
  if (fraction === null || fraction === undefined || Number.isNaN(fraction)) {
    return '—';
  }
  return `${(fraction * 100).toFixed(digits)}%`;
}

/** Compact large numbers: 1234 → "1.2K", 2_500_000 → "2.5M". */
export function fmtCompact(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(n);
}

/** Date-only label, no time-of-day. */
export function fmtDay(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function relativeTime(value: string | Date | null | undefined): string {
  if (!value) return 'never';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return 'never';
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
